import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../redux/reducers';
import { formatDate } from '../../../utils/stringUtils';
import {
  BankTransactionParticipation,
  CreateBankTransactionModel,
  TransactionType,
  BankMutation,
} from '../../../types/bankAccount';
import Button from '../buttons/Button';
import { ConnectionType } from '../../../redux/slices/connectionsSlice';
import ModalLayout from '../layouts/ModalLayout';
import Labeled from '../misc/Labeled';
import { formatEuroCentsWithSign } from '../../../utils/bankAccountUtil';
// Amount inputs handled by SplitMembersList
import SplitMembersList from '../financial/SplitMembersList';
import Slider from '../misc/Slider';

interface AssignSplitTransactionModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  transaction: CreateBankTransactionModel;
  mutation?: BankMutation;
  onAssignSuccess: (transaction: CreateBankTransactionModel) => void;
}

type AssignmentType = 'ENTIRE_GROUP' | 'INDIVIDUALS';

type BankTransactionState = {
  description: string;
  date: Date;
  amount: number;
  transactionType?: TransactionType;
  selectedMembers: number[];
  memberValues: Record<number, number>;
  memberRations?: Record<number, number | undefined>;
  assignmentType?: AssignmentType;
};

const getEmptyTransactionState = (
  mutation?: BankMutation,
  initialTransaction?: CreateBankTransactionModel,
) => {
  const transactionInfo = mutation || initialTransaction;
  return {
    description: initialTransaction?.description || '',
    date: new Date(),
    amount: transactionInfo?.amount ?? 0,
    selectedMembers: [],
    memberValues: {},
    memberRations: {},
    assignmentType: undefined,
  };
};

export default function AssignSplitTransactionModal({
  open,
  setOpen,
  transaction: initialTransaction,
  mutation,
  onAssignSuccess,
}: AssignSplitTransactionModalProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const connections = useSelector((state: RootState) => state.connections);

  const [transaction, setTransaction] = useState<BankTransactionState>(
    getEmptyTransactionState(mutation, initialTransaction),
  );

  // Filter connections to only show business members
  const businessMembers = connections.filter(
    (connection) => connection.type === ConnectionType.B2C_CONNECTION,
  );

  // Calculate remaining amount (sign-agnostic)
  const remainingAmount = useMemo(() => {
    if (transaction.assignmentType === 'ENTIRE_GROUP') return 0;
    const totalAssignedAbs = Object.values(transaction.memberValues).reduce(
      (sum, value) => sum + Math.abs(value || 0),
      0,
    );
    return Math.abs(transaction.amount) - totalAssignedAbs;
  }, [transaction.assignmentType, transaction.memberValues, transaction.amount]);

  // Handle assignment type change
  const handleAssignmentTypeChange = (assignmentType: AssignmentType) => {
    setTransaction((prev) => ({
      ...prev,
      assignmentType,
      selectedMembers: [],
      memberValues: {},
    }));
  };

  // Member selection removed in favor of always-visible rows

  // Select all replaced by Split/Reset button

  // Rations recalculation (proportional on remaining)
  const recalcFromRations = (
    prev: BankTransactionState,
    override?: {
      memberValues?: Record<number, number>;
      memberRations?: Record<number, number | undefined>;
    },
  ): BankTransactionState => {
    const { amount } = prev;
    const totalAbs = Math.abs(amount);
    const memberValues = override?.memberValues ?? prev.memberValues ?? {};
    const memberRations = override?.memberRations ?? prev.memberRations ?? {};

    const manualIds = Object.keys(memberValues)
      .map((id) => +id)
      .filter((id) => memberRations[id] === undefined);
    const manualAbsSum = manualIds.reduce((sum, id) => sum + Math.abs(memberValues[id] || 0), 0);
    const remainingAbs = Math.max(0, totalAbs - manualAbsSum);

    const rationEntries = Object.entries(memberRations)
      .map(([k, v]) => ({ id: +k, r: v }))
      .filter((e) => e.r !== undefined);
    const sumRations = rationEntries.reduce((sum, e) => sum + (e.r || 0), 0);

    const newMemberValues: Record<number, number> = { ...memberValues };
    if (sumRations > 0 && remainingAbs > 0) {
      let allocatedAbs = 0;
      rationEntries.forEach((e) => {
        const r = e.r || 0;
        if (r <= 0) {
          newMemberValues[e.id] = 0;
        } else {
          const shareAbs = Math.floor((remainingAbs * r) / sumRations);
          newMemberValues[e.id] = shareAbs;
          allocatedAbs += shareAbs;
        }
      });
      let remainder = remainingAbs - allocatedAbs;
      rationEntries.some((e) => {
        if (remainder <= 0) return true;
        if ((e.r || 0) > 0) {
          newMemberValues[e.id] = (newMemberValues[e.id] || 0) + 1;
          remainder -= 1;
        }
        return false;
      });
    } else {
      rationEntries.forEach((e) => {
        if ((e.r || 0) === 0) newMemberValues[e.id] = 0;
      });
    }

    return { ...prev, memberValues: newMemberValues, memberRations };
  };

  // Handle member value change (clamped, sets member to manual)
  const handleValueChange = (memberId: number, value: number) => {
    setTransaction((prev) => {
      const valueInCentsAbs = Math.max(0, Math.floor((value || 0) * 100));
      const memberRations = { ...(prev.memberRations ?? {}) };
      memberRations[memberId] = undefined;

      const pre = recalcFromRations(prev, { memberRations });
      const otherMembersTotalAbs = Object.entries(pre.memberValues)
        .filter(([id]) => +id !== memberId)
        .reduce((sum, [, v]) => sum + Math.abs(v || 0), 0);
      const maxForThisMemberAbs = Math.max(0, Math.abs(prev.amount) - otherMembersTotalAbs);
      const clampedAbs = Math.min(maxForThisMemberAbs, valueInCentsAbs);

      const updatedMemberValues = {
        ...pre.memberValues,
        [memberId]: clampedAbs,
      };

      return recalcFromRations(
        { ...pre, memberValues: updatedMemberValues },
        {
          memberValues: updatedMemberValues,
          memberRations,
        },
      );
    });
  };

  // Current amount formatting handled by SplitMembersList

  // Handle save
  const handleSave = () => {
    let participations: BankTransactionParticipation[] | undefined;

    if (transaction.assignmentType === 'INDIVIDUALS') {
      // Build participations from non-zero member values with the correct sign
      const sign = transaction.amount >= 0 ? 1 : -1;
      const participatingMembers = Object.entries(transaction.memberValues)
        .filter(([, value]) => (value || 0) > 0)
        .map(([id]) => +id);
      if (participatingMembers.length === 0) {
        return; // nothing to save
      }

      participations = participatingMembers.map((memberId) => ({
        connectionId: memberId,
        amount: sign * (transaction.memberValues[memberId] || 0),
      }));
    }
    // For ENTIRE_GROUP, we don't set participations as the backend handles equal distribution

    const updatedTransaction: CreateBankTransactionModel = {
      ...initialTransaction,
      description: transaction.description,
      date: transaction.date,
      amount: transaction.amount,
      participations,
      transactionType:
        transaction.assignmentType === 'ENTIRE_GROUP'
          ? transaction.amount >= 0
            ? TransactionType.GROUP_INCOME
            : TransactionType.GROUP_EXPENSE
          : transaction.amount >= 0
            ? TransactionType.PERSONAL_INCOME
            : TransactionType.PERSONAL_EXPENSE,
    };

    onAssignSuccess(updatedTransaction);
  };

  // Split equally among all business members
  const splitEqually = () => {
    setTransaction((prev) => {
      const selected = businessMembers.map((m) => +m.id);
      if (selected.length === 0) return prev;
      const totalAbs = Math.abs(prev.amount);
      const equalAbs = Math.floor(totalAbs / selected.length);
      const remainder = totalAbs % selected.length;
      const memberValues: Record<number, number> = {};
      const memberRations: Record<number, number | undefined> = {};
      selected.forEach((id, index) => {
        memberValues[id] = equalAbs + (index < remainder ? 1 : 0);
        memberRations[id] = undefined;
      });
      return { ...prev, selectedMembers: selected, memberValues, memberRations };
    });
  };

  // Reset all selections and values
  const resetSplit = () => {
    setTransaction((prev) => ({
      ...prev,
      selectedMembers: [],
      memberValues: {},
      memberRations: {},
    }));
  };

  const isResetable = useMemo(
    () => Object.values(transaction.memberValues).some((v) => (v || 0) > 0),
    [transaction.memberValues],
  );

  // Initialize transaction state when modal opens
  useEffect(() => {
    if (open) {
      setTransaction(getEmptyTransactionState(mutation, initialTransaction));
    }
  }, [open, mutation, initialTransaction]);

  return (
    <ModalLayout open={open} setOpen={setOpen} className="w-full max-w-2xl rounded-lg" closeButton>
      <div className="rounded-md bg-secondary-50 p-6">
        <h2 className="mb-4 text-lg font-medium">
          {t('page.financial.splitModal.assignSplitTransaction')}
        </h2>

        {/* Transaction Summary */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">
                {t('page.financial.splitModal.date')}
              </p>
              <p className="text-base font-semibold">{formatDate(transaction.date)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">
                {t('page.financial.splitModal.description')}
              </p>
              <p className="text-base font-semibold">{transaction.description}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">
                {t('page.financial.splitModal.amount')}
              </p>
              <p className="text-base font-semibold">
                {formatEuroCentsWithSign(transaction.amount, i18n.language)}
              </p>
            </div>
          </div>
        </div>

        {/* Assign Flow */}
        <div className="mb-4">
          <div className="mb-2 flex flex-row gap-4">
            <div className="flex-1">
              <Labeled
                label={t('page.financial.splitModal.description')}
                htmlFor="transaction-description-textarea">
                <textarea
                  className="h-[unset] w-full"
                  rows={2}
                  id="transaction-description-textarea"
                  placeholder={t('page.financial.splitModal.descriptionPlaceholder')}
                  value={transaction.description}
                  onChange={(ev) =>
                    setTransaction((prevTns) => ({
                      ...prevTns,
                      description: ev.target.value,
                    }))
                  }
                />
              </Labeled>
            </div>
          </div>

          {/* Assignment Type Selection */}
          <div className="mb-6">
            <Slider
              label={t('page.financial.splitModal.assignmentTypeLabel', {
                type: transaction.amount < 0 ? 'expense' : 'income',
              })}
              items={[
                {
                  label: t('page.financial.splitModal.entireGroup'),
                  value: 'ENTIRE_GROUP' as AssignmentType,
                },
                {
                  label: t('page.financial.splitModal.individuals'),
                  value: 'INDIVIDUALS' as AssignmentType,
                },
              ]}
              value={transaction.assignmentType}
              onChange={handleAssignmentTypeChange}
            />
          </div>

          {transaction.assignmentType === 'INDIVIDUALS' && (
            <>
              <SplitMembersList
                members={businessMembers.map((m) => ({
                  id: +m.id,
                  label: `${m.fields.FIRST_NAME} ${m.fields.LAST_NAME}`,
                }))}
                memberValues={transaction.memberValues}
                memberRations={transaction.memberRations}
                onSetValue={(id, v) => handleValueChange(id, v)}
                onSetRations={(id, r) =>
                  setTransaction((prev) =>
                    recalcFromRations(prev, {
                      memberRations: { ...(prev.memberRations ?? {}), [id]: r },
                    }),
                  )
                }
                headerTitle={t('page.financial.splitModal.selectMembers')}
                onSplit={splitEqually}
                onClear={resetSplit}
                isResetable={isResetable}
                remainingAmount={remainingAmount}
                remainingLabel={t('page.financial.splitModal.remaining')}
              />
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="primary-dark"
            className="bg-secondary-200 text-primary-900"
            onClick={() => setOpen(false)}>
            {t('page.financial.splitModal.cancelButton')}
          </Button>

          <Button
            variant="primary-dark"
            onClick={handleSave}
            disabled={
              !transaction.assignmentType ||
              Math.abs(remainingAmount) > 1 || // Allow 1 cent tolerance for floating point precision
              !transaction.amount ||
              (transaction.assignmentType === 'INDIVIDUALS' &&
                Object.values(transaction.memberValues).every((v) => (v || 0) === 0))
            }>
            {t('page.financial.splitModal.assignButton')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
